import { money } from '../../lib/api';
import type { Identity } from '../../shared/types';
import Hero from './Hero';
import HomeGrid, { type HomeTileDef } from './HomeGrid';
import ModuleTiles, { TILE_STYLES, type Tile } from './ModuleTiles';
import MoneyCard from './cards/MoneyCard';
import HealthCard from './cards/HealthCard';
import WellnessCard from './cards/WellnessCard';
import NeedsYou from './cards/NeedsYou';
import VerseCard from '../verse/VerseCard';
import MessageBoardCard from '../messages/MessageBoardCard';
import { KidMoneyCard } from '../kidmoney/KidMoneyCard';
import WeatherCard from '../weather/WeatherCard';
import BirthdaysCard from '../birthdays/BirthdaysCard';
import DinnerCard from './cards/DinnerCard';
import UpNextCard from './cards/UpNextCard';
import WhereCard from './cards/WhereCard';
import AskCard from './cards/AskCard';
import { useResource } from './useResource';
import { addDays, isoDate, localPeriod, localWeekStart } from './dates';
import {
  colorFor,
  dinnerFor,
  fetchEvents,
  fetchFamily,
  fetchHealth,
  fetchLocations,
  fetchNotifications,
  fetchInsuranceStatus,
  fetchPendingApprovals,
  fetchPlan,
  fetchStatus,
  fetchUncategorized,
  fetchWellness,
  unreadCount,
  upcoming,
} from './homeData';

/**
 * The household right now, for Brian and Bari: money first (it's the reason the
 * app exists), then the family's evening — dinner, calendar, where everyone is.
 */
export default function AdultHome({ identity }: { identity: Identity }) {
  const today = isoDate();
  const period = localPeriod();
  const week = localWeekStart();
  const from = today;
  const to = isoDate(addDays(new Date(), 8));

  const family = useResource('family', fetchFamily);
  const status = useResource(period, () => fetchStatus(period));
  const health = useResource(period, () => fetchHealth(period));
  const wellness = useResource('wellness', fetchWellness);
  const uncategorized = useResource(period, () => fetchUncategorized(period));
  const plan = useResource(week, () => fetchPlan(week));
  const events = useResource(`${from}|${to}`, () => fetchEvents(from, to));
  const locations = useResource('locations', fetchLocations);
  const notifications = useResource('notifications', fetchNotifications);
  const approvals = useResource('approvals', fetchPendingApprovals);
  const coverage = useResource('coverage', fetchInsuranceStatus);

  const color = colorFor(identity, family.data);
  const dinner = dinnerFor(plan.data, today)?.meal ?? null;
  const nextEvent = upcoming(events.data, 1)[0] ?? null;
  const unread = unreadCount(notifications.data);
  const pending = approvals.data?.length ?? 0;
  const lapsedLines = coverage.data?.lapsed ?? 0;
  const expiringLines = coverage.data?.expiring ?? 0;
  const uncatCount = uncategorized.data?.length ?? 0;

  // A read that failed is not a zero. Every tile hint below reports what it knows
  // and says so when it doesn't know — "Nothing to approve" on the back of a 404
  // is the hub lying to the only person who can act.
  const failed =
    uncategorized.error !== null ||
    approvals.error !== null ||
    notifications.error !== null ||
    plan.error !== null ||
    events.error !== null ||
    coverage.error !== null;

  const spent = (status.data ?? []).reduce((sum, s) => sum + s.spent, 0);
  const caps = (status.data ?? []).reduce((sum, s) => sum + s.monthly_cap, 0);
  const withLocation = (locations.data ?? []).length;
  const householdSize = (family.data ?? []).filter((m) => m.active).length;

  const tiles: Tile[] = [
    {
      to: '/birthdays',
      label: 'Birthdays',
      hint: 'Who’s coming up',
      ...TILE_STYLES.birthdays,
    },
    {
      to: '/reminders',
      label: 'Reminders',
      hint: 'Nudges for the house',
      ...TILE_STYLES.reminders,
    },
    {
      to: '/ask',
      label: 'Ask',
      hint: 'A little help',
      ...TILE_STYLES.ask,
    },

    {
      to: '/money',
      label: 'Money',
      hint: status.error
        ? "Couldn't load the budget"
        : caps > 0
          ? `${money(spent)} of ${money(caps)} this month`
          : 'Set your first cap',
      ...TILE_STYLES.money,
    },
    {
      to: '/meals',
      label: 'Meals',
      hint: plan.error
        ? "Couldn't load meals"
        : dinner
          ? `Tonight: ${dinner.name}`
          : 'Nothing planned tonight',
      ...TILE_STYLES.meals,
    },
    {
      to: '/chores',
      label: 'Chores',
      hint: approvals.error
        ? "Couldn't check approvals"
        : pending > 0
          ? `${pending} waiting on you`
          : 'Nothing to approve',
      ...TILE_STYLES.chores,
    },
    {
      to: '/calendar',
      label: 'Calendar',
      hint: events.error
        ? "Couldn't load the calendar"
        : nextEvent
          ? `Next: ${nextEvent.title}`
          : 'Nothing this week',
      ...TILE_STYLES.calendar,
    },
    {
      to: '/location',
      label: 'Where',
      hint:
        locations.error || family.error
          ? "Couldn't load locations"
          : withLocation > 0
            ? `${withLocation} of ${householdSize || withLocation} sharing`
            : 'Not connected yet',
      ...TILE_STYLES.location,
    },
    {
      to: '/vault',
      label: 'House',
      hint: 'Codes, accounts, providers',
      ...TILE_STYLES.house,
    },
    {
      to: '/money/insurance',
      label: 'Insurance',
      // A read that failed is never reported as "all clear" — same rule as the tiles
      // above. And a lapsed REQUIRED line is the loudest thing this hub can say.
      hint: coverage.error
        ? "Couldn't load coverage"
        : lapsedLines > 0
          ? `${lapsedLines} not in force`
          : expiringLines > 0
            ? `${expiringLines} renewing soon`
            : coverage.data
              ? 'All in force'
              : 'Policies and renewals',
      ...TILE_STYLES.insurance,
    },
    {
      to: '/notifications',
      label: 'Alerts',
      hint: notifications.error
        ? "Couldn't load alerts"
        : unread > 0
          ? `${unread} unread`
          : 'All caught up',
      ...TILE_STYLES.notifications,
    },
    {
      to: '/admin',
      label: 'Settings',
      hint: 'Categories, caps, family',
      ...TILE_STYLES.settings,
    },
  ];

  // The editable bento. Order + default sizes are the starting arrangement; each person
  // can reorder and resize from "Edit layout", saved to their own profile.
  const homeTiles: HomeTileDef[] = [
    { key: 'money', label: 'Money', defaultSize: 'wide', node: <MoneyCard res={status} /> },
    { key: 'health', label: 'Health', defaultSize: 'small', node: <HealthCard res={health} /> },
    // 'Health' above is the BUDGET score. This is the medical one — different card,
    // different endpoint, which is why the module is named wellness.
    { key: 'wellness', label: 'Wellness', defaultSize: 'small', node: <WellnessCard res={wellness} /> },
    { key: 'board', label: 'Board', defaultSize: 'tall', node: <MessageBoardCard /> },
    { key: 'dinner', label: 'Dinner', defaultSize: 'small', node: <DinnerCard res={plan} date={today} /> },
    { key: 'upnext', label: 'Up next', defaultSize: 'small', node: <UpNextCard res={events} family={family.data} /> },
    { key: 'where', label: 'Where', defaultSize: 'small', node: <WhereCard res={locations} family={family} /> },
    { key: 'weather', label: 'Weather', defaultSize: 'small', node: <WeatherCard /> },
    { key: 'kid_money', label: 'Kid money', defaultSize: 'wide', node: <KidMoneyCard /> },
    { key: 'birthdays', label: 'Birthdays', defaultSize: 'small', node: <BirthdaysCard /> },
    { key: 'ask', label: 'Ask', defaultSize: 'small', node: <AskCard /> },
    { key: 'verse', label: 'Verse', defaultSize: 'small', node: <VerseCard /> },
  ];

  return (
    <div className="space-y-4">
      {/* The greeting and what-needs-you stay fixed above the editable grid. */}
      <Hero name={identity.name ?? 'there'} color={color}>
        {summary({
          uncatCount,
          pending,
          unread,
          failed,
          dinnerName: dinner?.name ?? null,
          nextEventTitle: nextEvent?.title ?? null,
        })}
      </Hero>

      <NeedsYou
        uncategorized={uncategorized}
        approvals={approvals}
        notifications={notifications}
      />

      <HomeGrid tiles={homeTiles} />

      <ModuleTiles tiles={tiles} />
    </div>
  );
}

/**
 * One honest line about the day: counts, not cheerleading. When nothing is
 * asking for a human, the "nothing needs you" banner below already says so — so
 * the hero spends its line on the family's day instead of repeating it.
 *
 * `failed` is load-bearing: with a read missing, the hero must not settle into
 * "a quiet one — nothing on the books", because it doesn't know that.
 */
function summary({
  uncatCount,
  pending,
  unread,
  failed,
  dinnerName,
  nextEventTitle,
}: {
  uncatCount: number;
  pending: number;
  unread: number;
  failed: boolean;
  dinnerName: string | null;
  nextEventTitle: string | null;
}) {
  const parts: string[] = [];
  if (uncatCount > 0)
    parts.push(`${uncatCount} transaction${uncatCount === 1 ? '' : 's'} to categorize`);
  if (pending > 0) parts.push(`${pending} chore${pending === 1 ? '' : 's'} to approve`);
  if (unread > 0) parts.push(`${unread} unread`);

  if (parts.length > 0)
    return (
      <>
        {parts.join(' · ')}.{failed && ' Some of today’s list didn’t load.'}
      </>
    );
  if (failed) return <>Some of today’s list didn’t load — the card below has the details.</>;
  if (dinnerName && nextEventTitle)
    return (
      <>
        Dinner tonight is {dinnerName}. Next up: {nextEventTitle}.
      </>
    );
  if (dinnerName) return <>Dinner tonight is {dinnerName}.</>;
  if (nextEventTitle) return <>Next up: {nextEventTitle}.</>;
  return <>A quiet one — nothing on the books.</>;
}
